import Container from "juis-components/Container.js";
import SessionHandler, {SESSION_HANDLER_EVENTS} from "./SessionHandler.js";
import App from "./hewecon/models/App.js";
import getRequestAccessPage from "./hewecon/views/no-access/getNoAccessPage.js";
import {extendComponent} from "juis-components/ComponentUtils.js";
import {NAVIGATE} from "juis-router/Events.js";
import LoginPage from "./hewecon/views/login-page/index.js";
import CustomerForm from "./hewecon/models/CustomerForm.js";
import Menu from "./hewecon/models/Menu.js";
import MenuItem from "./hewecon/models/MenuItem.js";
import BULMA from "./bulma_components/bulmaCssClasses.js";
import TextComponent from "./components/TextComponent.js";
import getAppRouter from "./getAppRouter.js";
import getAnonymousRouter from "./getAnonymousRouter.js";
import HTML from "./utils/HTML.js";
import {createIcon} from "./bulma_components/Icon.js";
import PromiseButton from "./components/PromiseButton.js";
import Linguist from "../lib/JuiS/Linguist.js";
import appViewLexicons from "./hewecon/views/apps/lexicons/appViewLexicons.js";
import handleModificationEvents from "./utils/handleModificationEvents.js";
import {NOT_FOUND} from "juis-commons/Errors.js";
import Button from "./bulma_components/Button.js";
import {BILLING_STATUS_LOADED, TOP_UP_PAGE_OPENED} from "./hewecon/billingEvents.js";

const SHOW_APP_MENU = Symbol();
const linguist = new Linguist(appViewLexicons);
const appView = extendComponent(Container, function () {
    this.when(NAVIGATE).then(event => {
        let appCode = event.parameters["appCode"];
        const sessionUser = SessionHandler.getCurrentUser();
        if (!sessionUser) {
            // A visitor with no session may open a public customer form of an app that has a form allowing it (the
            // server only lists those for them). Any other page of the app still needs a login, so the anonymous
            // router, which only knows the form page, is used only when that is what was asked for.
            const asksForPublicForm = new RegExp(`^/${appCode}/public-customer-forms/`).test(window.location.pathname);
            if (asksForPublicForm) {
                CustomerForm.getAllPublic(appCode).then(response => {
                    this.content = response.data.length > 0 ? getAnonymousRouter() : new LoginPage();
                }).catch(() => {
                    this.content = new LoginPage();
                });
            } else {
                this.content = new LoginPage();
            }
        } else if (sessionUser.appCodeList.includes(appCode)) {
            initializeApp(appCode, sessionUser);
        } else {
            CustomerForm.getAllPublic(appCode).then(response => {
                if (response.data.length > 0) {
                    initializeApp(appCode, sessionUser);
                } else {
                    this.content = getRequestAccessPage(appCode, sessionUser);
                }
            });
        }
        this.bindListener(SessionHandler.on(
            [SESSION_HANDLER_EVENTS.SESSION_STARTED, SESSION_HANDLER_EVENTS.SESSION_UPDATED],
            sessionUser => initializeApp(appCode, sessionUser)
        ));

        document.addEventListener("visibilitychange", () => {
            if (!document.hidden && document.body.contains(this.getNode().getActualElement()) && SessionHandler.getCurrentUser()) {
                // Wait a second to allow device time to connect to network. This avoids "Failed to fetch" network errors.
                setTimeout(() => App.getByCode(appCode).then(loadAppSettings), 1000);
            }
        });
    });

    const getAdminItems = (app) => ({
        app,
        text: "Admin",
        link: "/settings-cards",
        children: []
    });

    const loadAppSettings = (app) => app.refreshLazyField(App.settings).then(() => app);

    const getMenuStructure = (userRole, app) => {
        const roleType = userRole?.role || null;
        const isAdmin = roleType === "ADMIN";
        let menu = userRole?.menu ?? app.settings.menus.find(foundMenu => foundMenu.role === roleType);
        if (!menu && isAdmin) {
            menu = app.settings.menus.find(foundMenu => foundMenu.role === "COORDINATOR");
        }
        if (!menu && isAdmin) {
            menu = app.settings.menus[0];
        }
        if (!menu) {
            menu = new Menu({text: "No menu available", items: []});
        } else {
            menu = menu.copy();
        }
        if (isAdmin) {
            menu.items.push(new MenuItem(getAdminItems(app)));
        }
        return menu;
    };


    const getNotificationBar = (icon, title, subtitle, style) => {
        return new Container(function () {
            this.left = new Container(function () {
                this.icon = createIcon(icon);
                this.icon.is3x = true;
                this.icon.addCssClass(BULMA.IS_PULLED_LEFT);
                this.icon.getNode().setStyle("width", "100%");
                this.icon.getNode().setStyle("height", "100%");
            }, [BULMA.COLUMN, "is-one-fifth"]);
            this.right = new Container(function () {
                this.title = new TextComponent(title, HTML.P, [BULMA.TITLE]);
                // The subtitle can also be a ready-made component, e.g. text with an inline icon.
                this.subtitle = typeof subtitle === "string"
                    ? new TextComponent(subtitle, HTML.P, [BULMA.SUBTITLE])
                    : subtitle;
            }, [BULMA.COLUMN]);
            this.getNode().addWrapper(HTML.DIV, [BULMA.HERO_BODY]);
            this.getNode().addWrapper(HTML.SECTION, [BULMA.HERO, BULMA.IS_SMALL, style]);
        }, ["hero-body", "columns", BULMA.IS_MARGINLESS], HTML.DIV);
    };

    const createDemoBar = (app) => {
        let demoBar = getNotificationBar("person-chalkboard", linguist.t("demo"), linguist.t("convertToRealTip"), BULMA.IS_PRIMARY);
        demoBar.right.appendChild(new PromiseButton(function () {
            this.icon = "handshake";
            this.text = linguist.t("convertToReal");
            this.isSmall = true;
            this.callback = () => {
                if (SessionHandler.getCurrentUser().anonymous) {
                    return this.navigate("/create-account", {});
                }
                return app.fetchCustomEndpoint(`${app.id}/demo-to-real`)
                    .then(() => window.location.reload(true));
            };
        }, [BULMA.IS_SMALL, BULMA.FIELD.CONTROL]));
        return demoBar;
    };
    const createArchivedBar = (app) => {
        return getNotificationBar("box-archive", "Archived", "This app is no longer maintained. It can be used as an archive.", BULMA.IS_DANGER);
    };
    const createBillingBar = (app, billingStatus) => {
        let bar;
        if (billingStatus.state === "UNPAID") {
            bar = getNotificationBar("triangle-exclamation", linguist.t("billingUnpaidTitle"),
                linguist.t("billingUnpaidSubtitle"), BULMA.IS_DANGER);
        } else if (billingStatus.state === "GRACE_PERIOD") {
            bar = getNotificationBar("clock", linguist.t("billingGracePeriodTitle"),
                linguist.t("billingGracePeriodSubtitle"), BULMA.IS_WARNING);
        }
        // Neither state blocks the app, so the banner can be dismissed until the next page load.
        const closeButton = new TextComponent("", "BUTTON", [BULMA.DELETE, BULMA.IS_PULLED_RIGHT]);
        closeButton.registerDomEvents("click");
        // destroy() would only remove the inner element and leave the hero section wrappers (the colored banner)
        // behind, so the whole node is hidden instead.
        closeButton.on("click", () => bar.getNode().togglePlaceholder(true));
        bar.right.insertBefore(closeButton, bar.right.title);

        bar.right.button = new Button(function () {
            this.text = linguist.t("topUp");
            this.icon = "coins";
            this.on("click", () => {
                return this.navigate(`/${app.code}/top-up`);
            });
        });
        return bar;
    };
    // The first time an owner sees the billing button, a banner explains what it is. It is remembered per user in
    // this browser, and only once the banner has been closed, so it stays until it has been read.
    const getBillingInfoSeenKey = () => `billingInfoSeen:${SessionHandler.getCurrentUser().username}`;
    const hasSeenBillingInfo = () => {
        try {
            return localStorage.getItem(getBillingInfoSeenKey()) === "1";
        } catch (e) {
            return true; // Without storage it could not be remembered, so it is not shown on every page load.
        }
    };
    const markBillingInfoSeen = () => {
        try {
            localStorage.setItem(getBillingInfoSeenKey(), "1");
        } catch (e) {
            // Nothing to do. It is just shown again next time.
        }
    };
    const createBillingInfoBar = () => {
        const subtitle = new Container(function () {
            this.before = new TextComponent(linguist.t("billingInfoBefore"));
            this.icon = createIcon("coins", "mx-1");
            this.after = new TextComponent(linguist.t("billingInfoAfter"));
        }, [BULMA.SUBTITLE], HTML.P);
        const bar = getNotificationBar("coins", linguist.t("billingInfoTitle"), subtitle, BULMA.IS_INFO);
        const closeButton = new TextComponent("", "BUTTON", [BULMA.DELETE, BULMA.IS_PULLED_RIGHT]);
        closeButton.registerDomEvents("click");
        closeButton.on("click", () => {
            markBillingInfoSeen();
            bar.getNode().togglePlaceholder(true);
        });
        bar.right.insertBefore(closeButton, bar.right.title);
        return bar;
    };
    // The top-up page shows the same information and has the form for paying, so the billing banners are hidden once
    // it has been opened (also if it was opened before the banner was loaded). They are not removed for good, and
    // the info banner isn't marked as seen: they are back on the next page load.
    const billingBars = [];
    let isTopUpPageOpen = false;
    const showBillingBar = (bar) => {
        this.insertBefore(bar);
        billingBars.push(bar);
        if (isTopUpPageOpen) {
            bar.getNode().togglePlaceholder(true);
        }
    };
    this.on(TOP_UP_PAGE_OPENED, () => {
        isTopUpPageOpen = true;
        billingBars.forEach(bar => bar.getNode().togglePlaceholder(true));
    });
    // The grace period is only worth a banner when it is about to end. With more than a month left it would just be noise.
    const isGraceEndingSoon = (billingStatus) => {
        if (!billingStatus.graceEndDate) {
            return true;
        }
        const limit = new Date();
        limit.setMonth(limit.getMonth() + 1);
        return new Date(billingStatus.graceEndDate) <= limit;
    };
    const initializeApp = (appCode, sessionUser) => {
        let role = sessionUser?.roles.find(foundRole => foundRole.appCode === appCode);
        // Not from the cache: a visitor's form page cached a stub of the app (id, code, name only), and the app
        // loaded for a signed-in user needs all of its fields.
        App.getByCode(appCode, undefined, true)
            .then(loadAppSettings)
            .then(app => {
                handleModificationEvents(app);
                const menu = getMenuStructure(role, app);
                if (app.demo) {
                    this.demoBar = createDemoBar(app);
                }
                if (app.archived) {
                    this.archivedBar = createArchivedBar(app);
                }
                this.content = getAppRouter(menu?.getUrl());

                let showBilling = false;
                if (sessionUser && !app.demo && !app.archived && app.monthlyPrice > 0) {
                    if (SessionHandler.isSuperAdmin() || app.owner === SessionHandler.getCurrentUser().username) {
                        showBilling = true;
                        app.getBillingStatus().then(billingStatus => {
                            this.trigger(BILLING_STATUS_LOADED, billingStatus);
                            const isGracePeriodFarFromEnd = billingStatus.state === "GRACE_PERIOD" && !isGraceEndingSoon(billingStatus);
                            // The info banner explains the billing, so the late payment banner waits until it has been closed.
                            if (app.owner === SessionHandler.getCurrentUser().username && !hasSeenBillingInfo()) {
                                showBillingBar(createBillingInfoBar());
                            } else if (billingStatus.state !== "PAID" && !isGracePeriodFarFromEnd) {
                                showBillingBar(createBillingBar(app, billingStatus));
                            }
                        });
                    }
                }
                this.trigger(SHOW_APP_MENU, {menu, app, showBilling});
            })
            .catch(e => {
                if (e.message === NOT_FOUND) {
                    this.content = getRequestAccessPage(appCode, sessionUser);
                } else {
                    throw e;
                }
            });
    };
});
export {appView as default, SHOW_APP_MENU};
